import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Dropdown, Spinner, Alert, Modal, ModalTitle, ModalBody, ModalFooter } from 'react-bootstrap';
import { FaPlus, FaCopy, FaClock, FaSave, FaTimes, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import moment from 'moment-timezone';
import DayRow from './DayRow';
import apiService from '../../services/ApiServices';

// Day mapping for additional data
const dayMapping = {
  'sun': { dayName: 'Sunday', initial: 'S' },
  'mon': { dayName: 'Monday', initial: 'M' },
  'tue': { dayName: 'Tuesday', initial: 'T' },
  'wed': { dayName: 'Wednesday', initial: 'W' },
  'thu': { dayName: 'Thursday', initial: 'T' },
  'fri': { dayName: 'Friday', initial: 'F' },
  'sat': { dayName: 'Saturday', initial: 'S' },
};

// Initial state for typical office hours with unique IDs for days
const initialWeeklyHours = Object.keys(dayMapping).map(id => ({
  id,
  ...dayMapping[id],
  slots: []
}));

const WeeklyHours = () => {
  const [weeklyHours, setWeeklyHours] = useState(initialWeeklyHours);
  const [copiedSlots, setCopiedSlots] = useState([]);
  const [selectedTimeZone, setSelectedTimeZone] = useState('Asia/Kolkata'); // Default to IST
  const [selectedAvailability, setSelectedAvailability] = useState('');

  const [allAvailities, setAllAvailabilities] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Alert states
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  const handleAddTimeSlot = (dayId) => {
    setWeeklyHours(prevHours =>
      prevHours.map(day =>
        day.id === dayId
          ? { ...day, slots: [...day.slots, { startTime: '09:00', endTime: '17:00' }] }
          : day
      )
    );
  };

  const handleRemoveTimeSlot = (dayId, slotIndex) => {
    setWeeklyHours(prevHours =>
      prevHours.map(day =>
        day.id === dayId
          ? { ...day, slots: day.slots.filter((_, i) => i !== slotIndex) }
          : day
      )
    );
  };

  const handleTimeChange = (dayId, slotIndex, field, value) => {
    setWeeklyHours(prevHours =>
      prevHours.map(day =>
        day.id === dayId
          ? {
            ...day,
            slots: day.slots.map((slot, i) =>
              i === slotIndex ? { ...slot, [field]: value } : slot
            ),
          }
          : day
      )
    );
  };

  const handleCopySlots = (dayId) => {
    const dayToCopy = weeklyHours.find(day => day.id === dayId);
    if (dayToCopy) {
      setCopiedSlots([...dayToCopy.slots]); // Deep copy
      showAlertMessage(`Time slots from ${dayToCopy.dayName} copied!`);
    }
  };

  const fetchAllAvailities = async () => {
    try {
      setLoading(true);
      // Replace with your actual API endpoint to fetch meetings
      const response = await apiService.get('/api/availability/user-avail');
      setAllAvailabilities(response.availabilities || []);
      const defaultId = response.availabilities?.[0]?.id || '';
      setSelectedAvailability(defaultId);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch meetings.');
      showAlertMessage(err.message || 'Failed to fetch meetings.', 'danger');
      setLoading(false);
    }
  };

  const fetchScheduleData = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiService.get(`/api/availability/${id}`);
      const { weekhours } = response.availabilities || {};
      if (weekhours) {
        const updatedHours = weekhours.map(day => ({
          ...dayMapping[day.id],
          id: day.id,
          slots: day.slots || [] // assuming slots is array of {startTime, endTime}
        }));
        setWeeklyHours(updatedHours);
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch schedule data.');
      showAlertMessage(err.message || 'Failed to fetch schedule data.', 'danger');
      console.error('Error fetching schedule data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedAvailability) {
      showAlertMessage('Please select a schedule first.', 'danger');
      return;
    }

    try {
      const payload = {
        weekhours: weeklyHours,
        availabilityid: selectedAvailability,
        timezone: selectedTimeZone
      };
      const response = await apiService.put('/api/availability/day', payload);
      if (response.success) {
        showAlertMessage('Weekly hours saved successfully!');
      } else {
        throw new Error(response.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving weekly hours:', err);
      showAlertMessage(err.message || 'Failed to save weekly hours.', 'danger');
    }
  };

  const handleCreateSchedule = () => {
    setNewScheduleName('');
    setShowCreateModal(true);
  };

  const handleDeleteSchedule = (id, name) => {
    setScheduleToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete) return;
    try {
      await apiService.delete(`/api/availability/${scheduleToDelete.id}`);
      await fetchAllAvailities();
      if (selectedAvailability === scheduleToDelete.id) {
        setSelectedAvailability(allAvailities.find(avail => avail.id !== scheduleToDelete.id)?.id || '');
      }
      setShowDeleteModal(false);
      setScheduleToDelete(null);
      showAlertMessage(`Schedule "${scheduleToDelete.name}" deleted successfully!`);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      showAlertMessage(err.message || 'Failed to delete schedule.', 'danger');
    }
  };

  const handleCreateSubmit = async () => {
    if (!newScheduleName.trim()) {
      showAlertMessage('Please enter a schedule name.', 'danger');
      return;
    }

    try {
      const payload = {
        name: newScheduleName.trim(),
        timezone: selectedTimeZone
      };
      const response = await apiService.post('/api/availability/', payload);
      if (response.success) {
        setShowCreateModal(false);
        // Refresh the full list to get the latest data
        await fetchAllAvailities();
        showAlertMessage(`Schedule "${newScheduleName}" created successfully!`);
      } else {
        throw new Error(response.message || 'Creation failed');
      }
    } catch (err) {
      console.error('Error creating schedule:', err);
      showAlertMessage(err.message || 'Failed to create schedule.', 'danger');
    }
  };

  const showAlertMessage = (message, variant = 'success') => {
    setAlertMessage(message);
    setAlertVariant(variant);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  useEffect(() => {
    fetchAllAvailities();
  }, []);

  useEffect(() => {
    fetchScheduleData(selectedAvailability);
  }, [selectedAvailability]);

  useEffect(() => {
    const loadTimeZones = () => {
      const zones = moment.tz.names().map((ianaName) => {
        const m = moment.tz(ianaName);
        const offset = m.format('Z'); // e.g., '+05:30'
        const abbr = m.format('z'); // e.g., 'IST'
        const label = `${ianaName} (${abbr}, UTC${offset})`;
        return { value: ianaName, label };
      });
      // Sort by offset for better UX
      zones.sort((a, b) => {
        const offsetA = moment.tz(a.value).utcOffset();
        const offsetB = moment.tz(b.value).utcOffset();
        return offsetA - offsetB;
      });
      setTimeZones(zones);
    };

    loadTimeZones();
  }, []);

  const handleTimeZoneChange = (e) => {
    setSelectedTimeZone(e.target.value);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const selectedScheduleName = allAvailities.find(avail => avail.id === selectedAvailability)?.name || 'Select Schedule';

  return (
    <>
      <Container style={{ maxWidth: '800px' }} className="mx-auto p-3 border rounded shadow-sm bg-white">
        {/* Header Section */}
        <Row className="align-items-center mb-4 pb-3 border-bottom">
          <Col md={6}>
            <h3 className="mb-0 fw-bold">
              <FaClock className="me-2" /> Schedule
            </h3>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Dropdown className="d-inline-block w-100">
              <Dropdown.Toggle variant="outline-primary" id="schedule-dropdown">
                {selectedScheduleName}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                {allAvailities.map((availability) => (
                  <Dropdown.Item key={availability.id} className="p-0">
                    <div className="d-flex justify-content-between align-items-center p-2">
                      <span
                        className="flex-grow-1 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedAvailability(availability.id)}
                      >
                        {availability.name}
                      </span>
                      <Button
                        size="sm"
                        variant="danger"
                        className="ms-2 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSchedule(availability.id, availability.name);
                        }}
                      >
                        <FaTrash size={10} />
                      </Button>
                    </div>
                  </Dropdown.Item>
                ))}
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleCreateSchedule} className="text-success fw-bold">
                  <FaPlus className="me-2" /> Create Schedule
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        {/* Weekly Hours Section */}
        <Row className="mb-4">
          <Col>
            <h5 className="fw-bold mb-1">
              <FaClock className="me-2" /> Weekly Hours
            </h5>
            <p className="text-muted mb-0">Set when you are typically available for meetings</p>
          </Col>
        </Row>

        {/* Days Rows */}
        <Row>
          <Col>
            {weeklyHours.map((day) => (
              <DayRow
                key={day.id}
                dayData={day}
                onAddTimeSlot={handleAddTimeSlot}
                onRemoveTimeSlot={handleRemoveTimeSlot}
                onTimeChange={handleTimeChange}
                onCopy={handleCopySlots}
              />
            ))}
          </Col>
        </Row>

        {/* Footer Section */}
        <Row className="mt-4 pt-3 border-top align-items-center">
          <Col md={6}>
            <Form.Select
              value={selectedTimeZone}
              onChange={handleTimeZoneChange}
              size="sm"
              aria-label="Time Zone"
            >
              {timeZones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              className="px-3 px-md-4 d-inline-flex align-items-center justify-content-center"
              style={{ whiteSpace: "nowrap" }}
            >
              <FaSave className="me-2" /> Save
            </Button>
          </Col>
        </Row>
      </Container>

      {/* Toast Alert */}
      {showAlert && (
        <Alert
          variant={alertVariant}
          className="position-fixed top-0 end-0 m-3"
          style={{ zIndex: 9999, minWidth: '300px' }}
        >
          {alertMessage}
        </Alert>
      )}

      {/* Create Schedule Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <ModalTitle>
            <FaPlus className="me-2" />
            Create New Schedule
          </ModalTitle>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Schedule Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter schedule name"
              value={newScheduleName}
              onChange={(e) => setNewScheduleName(e.target.value)}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            <FaTimes className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateSubmit}>
            Create
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Schedule Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <FaExclamationTriangle className="me-2" />
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body py-4 text-center">
                <FaTrash className="fs-1 text-danger mb-3 opacity-75" />
                <p className="mb-0 text-muted">
                  Are you sure you want to delete the schedule "{scheduleToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setShowDeleteModal(false)}>
                  <FaTimes className="me-2" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger px-4" onClick={confirmDeleteSchedule}>
                  <FaTrash className="me-2" />
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeeklyHours;